import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { getProgressFromStatus } from '../../../utils/taskProgress';
import useToast from '../../../hooks/useToast';


const ProjectCard = ({ project, userRole }) => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  // const { switchProject } = useProject(); // Unused since we use window.location
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);
  const menuRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowOptionsMenu(false);
      }
    };

    if (showOptionsMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showOptionsMenu]);

  // Safety check for undefined project
  if (!project) {
    return (
      <div className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm rounded-2xl border border-white/30 dark:border-slate-700 p-6">
        <div className="text-center text-slate-500">
          <p>Project data not available</p>
        </div>
      </div>
    );
  }

  // Calculate dynamic progress from tasks and project status
  // MATCHES ProjectOverview.jsx calculateProjectProgress() logic exactly
  const getProjectProgress = (project) => {
    // If project is marked as completed, always show 100%
    if (project.status && (project.status.toLowerCase() === 'completed' || project.status.toLowerCase() === 'complete')) {
      return 100;
    }

    // PRIORITY 1: Use backend stats completion_rate (matches ProjectOverview)
    if (project.backendStats && typeof project.backendStats.completion_rate === 'number') {
      return project.backendStats.completion_rate;
    }

    // PRIORITY 2: Use pre-calculated project.progress as fallback
    if (typeof project.progress === 'number') {
      return project.progress;
    }

    // Calculate progress from tasks if available (final fallback)
    if (project.tasks) {
      try {
        const tasks = typeof project.tasks === 'string' ? JSON.parse(project.tasks) : project.tasks;
        if (tasks && tasks.length > 0) {
          const progressSum = tasks.reduce((sum, task) => sum + getProgressFromStatus(task.column_name || task.columnName || task.status), 0);
          return progressSum / tasks.length;
        }
      } catch (error) {
        console.warn('Failed to parse project tasks:', error);
      }
    }

    // Fallback to 0
    return 0;
  };

  const handleProjectClick = () => {
    console.log('Opening project:', project?.id, project?.name);
    if (!project?.id) {
      console.error('Cannot open project: Missing ID');
      showToast('Error: Project ID is missing', 'error');
      return;
    }
    // Force a full navigation and data refresh
    window.location.href = `/project-management?id=${project.id}`;
  };

  const handleGoToBoard = () => {
    if (!project?.id) return;
    window.location.href = `/kanban-board?id=${project.id}`;
  };

  const handleDeleteProject = async (e) => {
    e.stopPropagation();
    setShowOptionsMenu(false);

    const confirmMessage = `Are you sure you want to delete "${project.name}"? This action cannot be undone.`;
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    // Double confirmation
    const doubleConfirm = window.prompt(`Type DELETE to confirm deletion of "${project.name}"`);
    if (doubleConfirm !== 'DELETE') {
      return;
    }

    try {
      const apiService = (await import('../../../utils/apiService')).default;
      await apiService.projects.delete(project.id);
      window.location.reload();
    } catch (error) {
      console.error('Failed to delete project:', error);
      showToast('Failed to delete project. Please try again.', 'error');
    }
  };

  const getStatusStyles = (status) => {
    const s = (status || 'active').toLowerCase();
    const colors = {
      'active': 'bg-emerald-100/80 text-emerald-700 border-emerald-200/50 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20',
      'completed': 'bg-blue-100/80 text-blue-700 border-blue-200/50 dark:bg-blue-500/10 dark:text-blue-400 dark:border-blue-500/20',
      'on hold': 'bg-amber-100/80 text-amber-700 border-amber-200/50 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/20',
      'overdue': 'bg-rose-100/80 text-rose-700 border-rose-200/50 dark:bg-rose-500/10 dark:text-rose-400 dark:border-rose-500/20'
    };
    return colors[s] || 'bg-slate-100/80 text-slate-700 border-slate-200 dark:bg-slate-700/20 dark:text-slate-400 dark:border-slate-600/30';
  };

  const getPriorityStyles = (priority) => {
    const p = (priority || 'medium').toLowerCase();
    switch (p) {
      case 'critical':
      case 'urgent':
        return {
          text: 'text-purple-600 dark:text-purple-400',
          bg: 'bg-purple-100 dark:bg-purple-900/30',
          label: 'Critical'
        };
      case 'high':
        return {
          text: 'text-red-600 dark:text-red-400',
          bg: 'bg-red-100 dark:bg-red-900/30',
          label: 'High'
        };
      case 'medium':
        return {
          text: 'text-amber-600 dark:text-amber-400',
          bg: 'bg-amber-100 dark:bg-amber-900/30',
          label: 'Medium'
        };
      case 'low':
        return {
          text: 'text-green-600 dark:text-green-400',
          bg: 'bg-green-100 dark:bg-green-900/30',
          label: 'Low'
        };
      default:
        return {
          text: 'text-slate-600 dark:text-slate-400',
          bg: 'bg-slate-100 dark:bg-slate-800',
          label: priority
        };
    }
  };

  const getProgressColor = (progress) => {
    if (progress >= 80) return 'bg-gradient-to-r from-emerald-400 to-green-500 shadow-[0_0_8px_rgba(16,185,129,0.3)]';
    if (progress >= 50) return 'bg-gradient-to-r from-blue-400 to-indigo-500 shadow-[0_0_8px_rgba(59,130,246,0.3)]';
    if (progress >= 25) return 'bg-gradient-to-r from-amber-400 to-orange-500 shadow-[0_0_8px_rgba(245,158,11,0.3)]';
    return 'bg-gradient-to-r from-rose-400 to-red-500 shadow-[0_0_8px_rgba(244,63,94,0.3)]';
  };

  const normalizedRole = userRole?.toLowerCase();
  const canEdit = normalizedRole === 'owner' || normalizedRole === 'admin' || normalizedRole === 'member';





  const handleArchiveProject = async (e) => {
    e.stopPropagation();
    setShowOptionsMenu(false);

    if (!window.confirm(`Are you sure you want to archive "${project.name}"?`)) {
      return;
    }

    try {
      const apiService = (await import('../../../utils/apiService')).default;
      await apiService.projects.update(project.id, { status: 'archived' });
      showToast('Project archived successfully!', 'success');
      setTimeout(() => window.location.reload(), 1500); // Give time for toast
    } catch (error) {
      console.error('Failed to archive project:', error);
      showToast('Failed to archive project. Please try again.', 'error');
    }
  };

  const handleExportData = (e) => {
    e.stopPropagation();
    setShowOptionsMenu(false);
    // TODO: Implement export functionality
    showToast('Export functionality will be implemented soon!', 'info');
  };

  return (
    <div
      className="group relative glass-card-premium p-6 cursor-pointer"
      onClick={handleProjectClick}
    >
      {/* Subtle gradient accent border on hover */}
      <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
           style={{
             background: 'linear-gradient(135deg, var(--color-primary-light) 0%, transparent 50%, var(--color-primary) 100%)'
           }}
      ></div>
      
      <div className="relative">
        <div className="flex items-start justify-between mb-5">
          <div className="flex-1">
            <h3 className="text-xl font-bold text-slate-800 dark:text-slate-100 mb-2 group-hover:aurora-text transition-all duration-300">
              {project?.name || 'Untitled Project'}
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 line-clamp-2 leading-relaxed">
              {project?.description || 'No description available'}
            </p>
          </div>
          <div className="flex items-center gap-2 ml-4">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border shadow-sm ${getStatusStyles(project?.status)}`}>
              {project?.status || 'Active'}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-slate-50 dark:bg-slate-700/50 rounded-xl flex items-center justify-center text-slate-500 dark:text-slate-400 group-hover:bg-primary/20 group-hover:text-primary transition-all duration-300 shadow-sm border border-slate-100 dark:border-slate-600/30">
              <Icon name="Calendar" size={18} />
            </div>
            <div className="flex flex-col">
              <span className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Due Date</span>
              <span className="text-xs font-bold text-slate-700 dark:text-slate-300">{project?.dueDate || 'Not set'}</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 ${getPriorityStyles(project?.priority).bg} rounded-xl flex items-center justify-center transition-all duration-300 shadow-sm border border-transparent`}>
              <Icon name="Flag" size={18} className={getPriorityStyles(project?.priority).text} />
            </div>
            <div className="flex flex-col">
              <span className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Priority</span>
              <span className={`text-xs font-bold ${getPriorityStyles(project?.priority).text}`}>
                {getPriorityStyles(project?.priority).label}
              </span>
            </div>
          </div>
        </div>

        <div className="mb-6 bg-slate-50/50 dark:bg-slate-900/20 p-4 rounded-xl border border-slate-100 dark:border-slate-700/30">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-tight">Project Progress</span>
            <span className="text-sm font-black text-slate-900 dark:text-slate-100">
              {Math.round(getProjectProgress(project))}%
            </span>
          </div>
          <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2.5 overflow-hidden p-[2px]">
            <div
              className={`h-full rounded-full transition-all duration-1000 ease-out ${getProgressColor(getProjectProgress(project))}`}
              style={{ width: `${getProjectProgress(project)}%` }}
            ></div>
          </div>
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-slate-100 dark:border-slate-700/50">
          <div className="flex items-center gap-2">
            <div className="flex -space-x-2">
              {[...Array(Math.min(3, project.member_count || 1))].map((_, i) => (
                <div key={i} className="w-7 h-7 rounded-full border-2 border-white dark:border-slate-800 bg-slate-100 dark:bg-slate-700 flex items-center justify-center overflow-hidden">
                  <Icon name="User" size={14} className="text-slate-400" />
                </div>
              ))}
              {(project.member_count > 3) && (
                <div className="w-7 h-7 rounded-full border-2 border-white dark:border-slate-800 bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center">
                  +{project.member_count - 3}
                </div>
              )}
            </div>
            <span className="text-[11px] font-bold text-slate-500 dark:text-slate-400 ml-1">
              {project.member_count || 0} Collaborative Members
            </span>
          </div>

          <div className="flex items-center gap-1">
            {canEdit && (
              <Button
                variant="ghost"
                size="sm"
                iconName="Edit"
                className="hover:bg-primary/20 hover:text-primary rounded-lg w-9 h-9 p-0"
                onClick={(e) => {
                  e.stopPropagation();
                  console.log('Edit project:', project.name);
                  navigate(`/project-management?id=${project.id}&tab=settings`);
                }}
              />
            )}
            <Button
              variant="ghost"
              size="sm"
              iconName="Kanban"
              className="hover:bg-primary/20 hover:text-primary rounded-lg w-9 h-9 p-0"
              onClick={(e) => {
                e.stopPropagation();
                handleGoToBoard();
              }}
              title="Go to Board"
            />
            <Button
              variant="ghost"
              size="sm"
              iconName="Eye"
              className="hover:bg-green-50 hover:text-green-600 rounded-lg w-9 h-9 p-0"
              onClick={(e) => {
                e.stopPropagation();
                handleProjectClick();
              }}
              title="View Project"
            />
            <Button
              variant="ghost"
              size="sm"
              iconName="Download"
              className="hover:bg-amber-50 hover:text-amber-600 dark:hover:bg-amber-900/20 dark:hover:text-amber-400 rounded-lg w-9 h-9 p-0"
              onClick={handleExportData}
              title="Export Data"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectCard;